import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoCrm from "@/assets/logo-d365-crm.png.asset.json";
import logoBc from "@/assets/logo-d365-business-central.png.asset.json";

interface Tier {
  tier: string;
  label: string;
  name: string;
  icon: string;
  tag: string;
  description: string;
  features: string[];
  ctaText: string;
  ctaLink: string;
}

const tiers: Tier[] = [
  {
    tier: "Tier 01",
    label: "Free & DIY",
    name: "Customer Care",
    icon: "🎓",
    tag: "Free for everyone",
    description:
      "Open to any Dynamics 365 user — not just UniverseIT clients. One free hour of support every month, best-in-the-world pricing on D365 licenses, plus free content and how-tos. No contract, no strings.",
    features: [
      "1 free hour of D365 support every month",
      "Best-in-the-world pricing on Dynamics 365 licenses",
      "Free how-tos, guides & content",
      "Open to any D365 user — no client relationship required",
    ],
    ctaText: "Explore Customer Care",
    ctaLink: "/customer-care",
  },
  {
    tier: "Tier 02",
    label: "Add-Ons",
    name: "D365 Add-Ons",
    icon: "⚡",
    tag: "Free & paid",
    description:
      "Free and paid add-ons that put your Dynamics 365 on steroids — built and supported by UniverseIT.",
    features: [
      "D365 SuperEmail (free) — easily use your CRM and/or Business Central data to send emails, also via Mailchimp",
      "D365 SuperImport — drag-and-drop importing & bulk update (coming soon)",
      "Built and supported by UniverseIT",
    ],
    ctaText: "See D365 Add-Ons",
    ctaLink: "/add-ons",
  },
  {
    tier: "Tier 03",
    label: "Help Desk",
    name: "D365 Help Desk",
    icon: "🎧",
    tag: "Public · Pay as you go",
    description:
      "Open to the public. Reach out for immediate Dynamics 365 support — just agree to our hourly billing terms and drop a $1 card hold to get started.",
    features: [
      "Open to everyone — no contract required",
      "Immediate, on-demand D365 support",
      "Simple hourly billing — agree to terms, place a $1 card hold",
      "CRM and Business Central support",
    ],
    ctaText: "Get D365 support",
    ctaLink: "/help-desk",
  },
  {
    tier: "Tier 04",
    label: "Client Success (CRM)",
    name: "D365 Client Success — CRM",
    icon: "✨",
    tag: "Dedicated resource",
    description:
      "A dedicated D365 resource to implement, maintain and support your Dynamics 365 CRM — Sales, Customer Service and Marketing.",
    features: [
      "Dedicated D365 CRM resource",
      "Implementation or ongoing maintenance & support",
      "Sales, Customer Service & Marketing",
      "Dashboards, workflows & automations",
    ],
    ctaText: "See Client Success — CRM",
    ctaLink: "/customer-success-crm",
  },
  {
    tier: "Tier 05",
    label: "Client Success (Business Central)",
    name: "D365 Client Success — Business Central",
    icon: "📊",
    tag: "Dedicated resource",
    description:
      "A dedicated D365 resource to implement, maintain and support your Dynamics 365 Business Central — finance, operations and inventory.",
    features: [
      "Dedicated Business Central resource",
      "Implementation or ongoing maintenance & support",
      "Finance, operations & inventory",
      "Reporting, integrations & automations",
    ],
    ctaText: "See Client Success — Business Central",
    ctaLink: "/customer-success-business-central",
  },
  {
    tier: "Tier 06",
    label: "Implementation",
    name: "D365 Implementations",
    icon: "🚀",
    tag: "SMB → Enterprise",
    description:
      "Best-in-class tools and certified consultants to implement Dynamics 365 CRM and ERP — from SMB to mid-market to enterprise.",
    features: [
      "SMB, mid-market and enterprise",
      "CRM and ERP (Business Central) implementations",
      "Best-in-class implementation tooling",
      "Scoping, migration, go-live & hypercare",
    ],
    ctaText: "See D365 Implementations",
    ctaLink: "/implementations",
  },
];

const M365Ladder = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hiddenRight, setHiddenRight] = useState(0);

  const scrollByCard = useCallback((dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.querySelector<HTMLElement>("[data-ladder-card]");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 20 : 480;
    track.scrollBy({ left: dir * cardWidth, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const update = () => {
      const firstCard = track.querySelector<HTMLElement>("[data-ladder-card]");
      const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 20 : 480;
      const idx = Math.round(track.scrollLeft / cardWidth);
      setActiveIndex(Math.min(tiers.length - 1, Math.max(0, idx)));
      const rightEdge = track.scrollLeft + track.clientWidth;
      const visibleCount = Math.floor((rightEdge - track.scrollLeft) / cardWidth + 0.05);
      const lastVisibleIdx = idx + visibleCount - 1;
      setHiddenRight(Math.max(0, tiers.length - 1 - lastVisibleIdx));
    };
    update();
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      track.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const goTo = (idx: number) => {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.querySelector<HTMLElement>("[data-ladder-card]");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 20 : 480;
    track.scrollTo({ left: idx * cardWidth, behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden bg-background pt-[calc(var(--header-offset,96px)+2rem)] pb-20 lg:pb-28">
      <div className="cosmic-glow absolute -top-40 left-1/2 -translate-x-1/2 pointer-events-none" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Label bar */}
        <div className="mx-auto mb-4 flex max-w-3xl items-center justify-center gap-3">
          <span className="eyebrow text-primary">Free</span>
          <span className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-primary/60 to-primary/10" />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Spectrum</span>
          <span className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-primary/60 to-primary/10" />
          <span className="eyebrow text-primary">Full-service</span>
        </div>

        <div className="mx-auto max-w-3xl text-center">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Unleash Dynamics 365
          </h1>
        </div>

        {/* D365 product surface */}
        <div className="mx-auto mt-8 max-w-3xl">
          {[
            { label: "CRM", logo: logoCrm.url, logoAlt: "Dynamics 365", apps: ["Sales", "Service", "Field Service", "Customer Insights", "Contact Center", "Project Operations"] },
            { label: "ERP", logo: logoBc.url, logoAlt: "Microsoft Dynamics 365 Business Central", apps: ["Business Central", "Finance & Operations"] },
          ].map((row) => (
            <div
              key={row.label}
              className="mb-3 grid grid-cols-[3rem_auto_1fr] items-center gap-x-3 gap-y-2"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground text-right">
                {row.label}
              </span>
              <span className="inline-flex items-center rounded-md bg-white px-3 py-2">
                <img
                  src={row.logo}
                  alt={row.logoAlt}
                  className="h-7 w-auto object-contain"
                />
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {row.apps.map((app) => (
                  <span
                    key={app}
                    className="inline-flex items-center rounded-full border border-cosmic-strong bg-card/60 px-3 py-1 text-xs font-medium text-foreground/90 backdrop-blur transition hover:border-primary/60 hover:text-primary sm:text-sm"
                  >
                    {app}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <p className="mt-6 text-center text-base font-medium text-primary sm:text-lg">
            Pair with Copilot and unleash like never before.
          </p>
        </div>

        {/* Controls */}
        <div className="mt-10 hidden items-center justify-end gap-3 sm:flex">
          <button
            type="button"
            aria-label="Previous"
            onClick={() => scrollByCard(-1)}
            disabled={activeIndex === 0}
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-cosmic-strong bg-card/60 text-foreground backdrop-blur transition hover:border-primary hover:text-primary disabled:opacity-40 disabled:hover:border-cosmic-strong disabled:hover:text-foreground"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => scrollByCard(1)}
            disabled={activeIndex >= tiers.length - 1}
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-cosmic-strong bg-card/60 text-foreground backdrop-blur transition hover:border-primary hover:text-primary disabled:opacity-40 disabled:hover:border-cosmic-strong disabled:hover:text-foreground"
          >
            <ChevronRight className="h-5 w-5" />
            {hiddenRight > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground shadow-md ring-2 ring-background">
                +{hiddenRight}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Full-bleed track */}
      <div
        ref={trackRef}
        className="ladder-track mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-6 px-4 sm:px-6 lg:px-8"
        style={{ scrollbarWidth: "none" }}
      >
        {tiers.map((t) => (
          <article
            key={t.tier}
            data-ladder-card
            className="group relative flex w-[85vw] max-w-[520px] shrink-0 snap-start flex-col rounded-[22px] border border-cosmic-strong bg-[image:var(--gradient-card)] p-7 shadow-medium transition hover:border-primary/60 hover:shadow-strong sm:w-[440px] lg:w-[480px] xl:w-[520px]"
          >
              <div className="flex items-center justify-between">
                <span className="eyebrow text-primary">
                  {t.tier} · {t.label}
                </span>
                <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {t.tag}
                </span>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <span className="text-4xl leading-none" aria-hidden="true">
                  {t.icon}
                </span>
                <h3 className="font-heading text-2xl font-bold text-foreground">{t.name}</h3>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.description}</p>

              <ul className="mt-6 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 pt-5 border-t border-cosmic">
                <Link
                  to={t.ctaLink}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition hover:text-primary/80"
                >
                  {t.ctaText}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          ))}
        </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Dots */}
        <div className="mt-4 flex items-center justify-center gap-2">
          {tiers.map((t, i) => (
            <button
              key={t.tier}
              type="button"
              aria-label={`Go to ${t.name}`}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === activeIndex ? "w-8 bg-primary" : "w-2 bg-foreground/20 hover:bg-foreground/40"
              }`}
            />
          ))}
        </div>

        {/* MSP Callout Band */}
        <div className="mt-14 overflow-hidden rounded-[20px] border border-cosmic-strong bg-[image:var(--gradient-primary)] shadow-cosmic">
          <div className="flex flex-col items-center justify-between gap-5 px-6 py-6 sm:px-10 sm:py-7 md:flex-row md:gap-8">
            <p className="text-center text-base font-medium text-primary-foreground md:text-left md:text-lg">
              <span className="font-bold">Customer Care is free.</span> Move your Microsoft licensing to us and we'll match your current vendor's price — and beat it.
            </p>
            <Button variant="cosmic" size="lg" asChild className="bg-background/95 text-primary hover:bg-background">
              <a href="https://universeit.com/schedule-a-call" target="_blank" rel="noopener noreferrer">
                Talk to us
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default M365Ladder;
